import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Redirect } from 'react-router-dom';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { pushPurchaseEvent } from '../../_helpers/analytics';

// Маппінг ID платіжних методів на ключі перекладу
const PAYMENT_METHOD_TRANSLATION_MAP = {
  'postpaid': 'postpaid',
  'invoice': 'invoiceCompany',
};

const OrderSuccess = ({ match, history }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isValidated, setIsValidated] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState('');

  const orderId = match?.params?.orderId;

  // Валідація замовлення через sessionStorage
  useEffect(() => {
    if (!orderId) {
      setIsUnauthorized(true);
      return;
    }

    // Отримуємо дані про замовлення з sessionStorage
    const storedOrderData = sessionStorage.getItem('lastOrderId');
    
    // Якщо немає даних про замовлення або вони не збігаються - редирект
    if (!storedOrderData || storedOrderData !== orderId) {
      // Невидимо редирект на /orders, щоб не показувати сторінку неавторизованого користувача
      setIsUnauthorized(true);
      return;
    }

    // Отримуємо ID обраної платіжної методи
    const paymentId = sessionStorage.getItem('lastOrderPaymentId');
    if (paymentId) {
      setPaymentMethodId(paymentId);
    }

    setIsValidated(true);

    // Очистити sessionStorage при виході зі сторінки (безпека)
    return () => {
      // Можемо не чистити одразу, щоб дозволити оновлення сторінки
      // Але можемо встановити час дії (15 хвилин)
    };
  }, [orderId]);

  useEffect(() => {
    if (!orderId || !isValidated) {
      return;
    }

    const storedAnalytics = sessionStorage.getItem('lastPurchaseAnalytics');
    if (!storedAnalytics) {
      return;
    }

    try {
      const purchasePayload = JSON.parse(storedAnalytics);
      const purchaseTransactionId = purchasePayload?.ecommerce?.transaction_id;

      if (
        purchasePayload?.event === 'purchase' &&
        (!purchaseTransactionId || String(purchaseTransactionId) === String(orderId))
      ) {
        pushPurchaseEvent(purchasePayload);
        sessionStorage.removeItem('lastPurchaseAnalytics');
      }
    } catch (error) {
      console.error('Failed to dispatch purchase analytics:', error);
    }
  }, [isValidated, orderId]);

  // Очистити sessionStorage коли користувач переходить на іншу сторінку
  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/order-success')) {
        sessionStorage.removeItem('lastOrderId');
        sessionStorage.removeItem('lastPurchaseAnalytics');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Якщо замовлення не валідне - перенаправити
  if (isUnauthorized) {
    return <Redirect to="/cart" />;
  }

  // Якщо ще не валідовано - не показувати нічого
  if (!isValidated) {
    return null;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="80vh"
      p={isMobile ? 2 : 3}
    >
      {/* Зображення успішного замовлення */}
      <Box
        component="img"
        src="/images/logos/order-success.svg"
        alt="Order Success"
        sx={{
          width: isMobile ? '80px' : '120px',
          height: isMobile ? '80px' : '120px',
          marginBottom: isMobile ? 2 : 3,
          objectFit: 'contain',
        }}
      />

      <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom align="center">
        {t('formed_successfully') || 'Ваше замовлення успішно оформлене'}
      </Typography>

      <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom align="center">
        {orderId
          ? `${t('id_zakazu') || 'Номер замовлення'}: ${orderId}`
          : t('order_number_not_found') || 'Номер замовлення не знайдено'}
      </Typography>

      <Typography color="textSecondary" align="center" paragraph sx={{ maxWidth: '500px' }}>
        {paymentMethodId
          ? `${t('selected_payment_method') || 'Вибрана методу оплати'}: ${t(PAYMENT_METHOD_TRANSLATION_MAP[paymentMethodId] || paymentMethodId) || paymentMethodId}`
          : t('order_success_description') || 'Ви можете переглянути деталі у своєму кабінеті.'}
      </Typography>

      {/* Адаптивна сітка кнопок */}
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? 1 : 2}
        mt={3}
        sx={{
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          '& button': {
            flex: isMobile ? 1 : 'auto',
          },
        }}
      >
        <Button
          component={Link}
          to="/profile/orders"
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          style={{
            borderRadius: '50px',
            marginRight: isMobile ? 0 : '15px',
            marginBottom: isMobile ? '15px' : 0,
          }}
        >
          {t('viewOrders') || 'Переглянути замовлення'}
        </Button>
        <Button
          component={Link}
          to="/"
          variant="outlined"
          color="primary"
          fullWidth={isMobile}
          style={{
            borderRadius: '50px',
          }}
        >
          {t('continueShopping') || 'Продовжити покупки'}
        </Button>
      </Box>
    </Box>
  );
};

export default OrderSuccess;
